<template>
  <div class="po-page">
    <loading-panel :loading="loading"></loading-panel>

    <breadcrumb class="po-breadcrumb" style="margin-left: -15px">
      <breadcrumb-item>
        <a href="#" @click.prevent="onCancel">Photo settings</a>
      </breadcrumb-item>
      <breadcrumb-item>
        <span>{{ headerTitle }}</span>
      </breadcrumb-item>
    </breadcrumb>

    <div class="po-editor" ref="editor" :style="editorStyle">
      <div class="po-topbar">
        <h4 class="text-white mb-0 po-title">{{ headerTitle }}</h4>
        <div class="po-topbar-right">
          <span class="badge-tag badge-tag--primary">{{ selectedAspectRatio.label }}</span>
          <save-status :dirty="dirty" :saved="mode === 1" />
        </div>
      </div>

      <div class="po-body">
        <!-- Layers panel: tools that create layers, then the list -->
        <div class="po-left">
          <input ref="imageInput" class="po-file" accept="image/*" type="file" @change="uploadImage" />
          <input ref="sampleInput" class="po-file" accept="image/*" type="file" @change="setSample" />
          <div class="po-tools">
            <button type="button" class="po-tool text-white" @click="addTextLayer">
              <i class="tim-icons icon-caps-small"></i><span>Text</span>
            </button>
            <button type="button" class="po-tool text-white" @click="$refs.imageInput.click()">
              <i class="tim-icons icon-image-02"></i><span>Image</span>
            </button>
            <button type="button" class="po-tool text-white" :class="{ 'po-tool--on': sample.url }" @click="$refs.sampleInput.click()">
              <i class="tim-icons icon-camera-18"></i><span>Sample</span>
            </button>
          </div>
          <div class="po-layers">
            <div class="po-sec-title po-layers-title text-white"><span>Layers</span><span v-if="layers.length" class="po-count">{{ layers.length }}</span></div>
            <p v-if="layers.length === 0" class="po-help po-layers-empty text-white">No layers yet. Add text or an image to start.</p>
            <draggable class="po-layers-list" v-model="layers" tag="div" handle=".data-table__handle" :animation="150" @end="onLayerReorder">
              <div
                v-for="layer in layers"
                :key="layer.id"
                class="po-layer"
                :class="{ 'po-layer--active': layer.id === activeLayerId }"
                @click="selectLayer(layer.id)"
              >
                <span class="data-table__handle" title="Drag to reorder" aria-label="Drag to reorder" @click.stop>
                  <i class="tim-icons el-icon-rank"></i>
                </span>
                <i class="tim-icons po-layer-icon text-white" :class="layer.type === 'text' ? 'icon-caps-small' : 'icon-image-02'"></i>
                <span class="po-layer__label text-white">{{ layerLabel(layer) }}</span>
                <button type="button" class="row-remove" :title="'Remove ' + layerLabel(layer)" :aria-label="'Remove ' + layerLabel(layer)" @click.stop="removeLayer(layer.id)">
                  <i class="tim-icons icon-simple-remove"></i>
                </button>
              </div>
            </draggable>
          </div>
        </div>

        <!-- Stage -->
        <div class="po-stage">
          <div class="po-stage-inner" ref="stage" @click.self="clearSelection">
            <div class="po-artboard" :class="{ 'po-artboard--photo': sample.url }">
              <canvas ref="canvas"></canvas>
            </div>
          </div>
          <div class="po-stagebar text-white">
            <span class="po-stagebar-size">{{ design.width }} by {{ design.height }} px</span>
            <span v-if="active.id" class="po-stagebar-muted po-stagebar-size">Selected at {{ active.x }}, {{ active.y }}</span>
            <template v-if="sample.url">
              <span class="po-stagebar-muted">Sample: {{ sample.name }}, {{ sample.width }} by {{ sample.height }}</span>
              <span v-if="sampleMismatch" class="po-pill po-pill--warn">Aspect differs, the overlay fits and centers</span>
              <base-button variant="outlined" tone="primary" size="sm" @click="clearSample">Remove sample</base-button>
            </template>
            <template v-else>
              <span class="po-stagebar-muted">Checkerboard shows where the photo will be. Use Sample to preview on a real photo.</span>
            </template>
            <div class="po-zoom">
              <span class="po-zoom-label">Zoom</span>
              <button type="button" class="po-zoom-btn text-white" aria-label="Zoom out" @click="zoomBy(1 / 1.25)">-</button>
              <span class="po-zoom-value" title="How large the artboard is shown on screen. Does not affect the photo.">{{ Math.round(zoom * 100) }}%</span>
              <button type="button" class="po-zoom-btn text-white" aria-label="Zoom in" @click="zoomBy(1.25)">+</button>
              <button type="button" class="po-zoom-btn po-zoom-btn--wide text-white" @click="fitCanvas">Fit</button>
            </div>
          </div>
        </div>

        <!-- Inspector -->
        <aside class="po-inspector">
          <div class="po-ins-scroll">
            <!-- Text layer -->
            <div v-if="active.id && active.type === 'text'" class="po-sec">
              <div class="po-sec-title text-white"><span>Text layer</span><button type="button" class="po-sec-action text-white" title="Delete layer" aria-label="Delete layer" @click="removeLayer(active.id)"><i class="tim-icons icon-trash-simple"></i></button></div>
              <base-input label="Text" v-model="active.text" @input="setText"></base-input>
              <div class="row">
                <div class="col-8">
                  <base-input label="Font family">
                    <el-select v-model="active.font" class="select-primary" filterable placeholder="Font" @change="onFontChange" @visible-change="onFontListToggle">
                      <el-option v-for="(f, i) in orderedFonts" :key="'F' + i" class="select-primary" :label="f.family" :value="f.family">
                        <span :style="{ fontFamily: '\'' + f.family + '\', sans-serif' }">{{ f.family }}</span>
                      </el-option>
                    </el-select>
                  </base-input>
                </div>
                <div class="col-4">
                  <base-input label="Size">
                    <el-input-number :controls="false" :min="4" :max="800" :precision="0" v-model="active.fontSize" @change="setFontSize" />
                  </base-input>
                </div>
              </div>
              <color-field class="po-colorfield" :value="active.color || '#000000'" label="Color" :presets="TEXT_COLOR_PRESETS" @input="setColor" />
              <div class="po-align">
                <label class="text-white">Text alignment</label>
                <div class="po-align-row">
                  <button type="button" class="po-align-btn text-white" :class="{ 'po-align-btn--on': active.align === 'left' }" title="Anchor left" @click="setTextAlign('left')"><svg viewBox="0 0 16 16"><path d="M2 3h12M2 6.5h7M2 10h12M2 13.5h7"/></svg></button>
                  <button type="button" class="po-align-btn text-white" :class="{ 'po-align-btn--on': active.align === 'center' }" title="Anchor center" @click="setTextAlign('center')"><svg viewBox="0 0 16 16"><path d="M2 3h12M4.5 6.5h7M2 10h12M4.5 13.5h7"/></svg></button>
                  <button type="button" class="po-align-btn text-white" :class="{ 'po-align-btn--on': active.align === 'right' }" title="Anchor right" @click="setTextAlign('right')"><svg viewBox="0 0 16 16"><path d="M2 3h12M7 6.5h7M2 10h12M7 13.5h7"/></svg></button>
                </div>
                <info-hint variant="neutral" class="po-hint">The dashed marker shows the anchor. When a dynamic variable is filled in, the text grows or shrinks from that point, so a centered anchor stays centered.</info-hint>
              </div>
              <div class="po-align">
                <label class="text-white">Align to artboard</label>
                <div class="po-align-row">
                  <button type="button" class="po-align-btn text-white" title="Align left" @click="alignLayer('left')"><svg viewBox="0 0 16 16"><path d="M2 1v14M5 4h7v3H5zM5 9h4v3H5z"/></svg></button>
                  <button type="button" class="po-align-btn text-white" title="Center horizontally" @click="alignLayer('center')"><svg viewBox="0 0 16 16"><path d="M8 1v14M4 4h8v3H4zM5.5 9h5v3h-5z"/></svg></button>
                  <button type="button" class="po-align-btn text-white" title="Align right" @click="alignLayer('right')"><svg viewBox="0 0 16 16"><path d="M14 1v14M4 4h7v3H4zM7 9h4v3H7z"/></svg></button>
                  <span class="po-align-gap"></span>
                  <button type="button" class="po-align-btn text-white" title="Align top" @click="alignLayer('top')"><svg viewBox="0 0 16 16"><path d="M1 2h14M4 5h3v7H4zM9 5h3v4H9z"/></svg></button>
                  <button type="button" class="po-align-btn text-white" title="Center vertically" @click="alignLayer('middle')"><svg viewBox="0 0 16 16"><path d="M1 8h14M4 4h3v8H4zM9 5.5h3v5H9z"/></svg></button>
                  <button type="button" class="po-align-btn text-white" title="Align bottom" @click="alignLayer('bottom')"><svg viewBox="0 0 16 16"><path d="M1 14h14M4 4h3v7H4zM9 7h3v4H9z"/></svg></button>
                </div>
                <small class="po-help text-white">Drag on the canvas to place freely. Arrow keys nudge by 1px, with Shift by 10px.</small>
              </div>
            </div>

            <!-- Dynamic variables, text layers only -->
            <div v-if="active.id && active.type === 'text'" class="po-sec">
              <div class="po-sec-title text-white"><span>Insert dynamic variable</span></div>
              <div class="po-chips">
                <button v-for="v in dynamic_variables" :key="'DV' + v.id" type="button" class="po-chip text-white" :title="v.description" @click="insertVariable(v.name)">{{ v.name.slice(1, -1) }}</button>
              </div>
              <small class="po-help text-white">Added to the end of the text. Replaced with the event details when the overlay is applied to a photo.</small>
            </div>

            <!-- Image layer -->
            <div v-else-if="active.id && active.type === 'image'" class="po-sec">
              <div class="po-sec-title text-white"><span>Image layer</span><button type="button" class="po-sec-action text-white" title="Delete layer" aria-label="Delete layer" @click="removeLayer(active.id)"><i class="tim-icons icon-trash-simple"></i></button></div>
              <div v-if="active.file_name" class="po-filename text-white" :title="active.file_name"><i class="tim-icons icon-image-02"></i><span>{{ active.file_name }}</span></div>
              <div class="row">
                <div class="col-6">
                  <base-input label="Width">
                    <el-input-number :controls="false" :min="1" :precision="0" v-model="active.width" @change="setImageWidth" />
                  </base-input>
                </div>
                <div class="col-6">
                  <base-input label="Height">
                    <el-input-number :controls="false" :min="1" :precision="0" v-model="active.height" @change="setImageHeight" />
                  </base-input>
                </div>
              </div>
              <div class="mb-3 po-toggle">
                <el-switch active-color="#e14eca" v-model="lockProportions" @change="setLockProportions" />
                <label class="text-white">Lock proportions
                  <el-popover trigger="hover" placement="left" width="240">
                    <div><div class="popover-body" style="word-break: normal; text-align: left">Keeps the image from stretching when you change its width or height or drag a corner.</div></div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
              <div class="po-align">
                <label class="text-white">Align to artboard</label>
                <div class="po-align-row">
                  <button type="button" class="po-align-btn text-white" title="Align left" @click="alignLayer('left')"><svg viewBox="0 0 16 16"><path d="M2 1v14M5 4h7v3H5zM5 9h4v3H5z"/></svg></button>
                  <button type="button" class="po-align-btn text-white" title="Center horizontally" @click="alignLayer('center')"><svg viewBox="0 0 16 16"><path d="M8 1v14M4 4h8v3H4zM5.5 9h5v3h-5z"/></svg></button>
                  <button type="button" class="po-align-btn text-white" title="Align right" @click="alignLayer('right')"><svg viewBox="0 0 16 16"><path d="M14 1v14M4 4h7v3H4zM7 9h4v3H7z"/></svg></button>
                  <span class="po-align-gap"></span>
                  <button type="button" class="po-align-btn text-white" title="Align top" @click="alignLayer('top')"><svg viewBox="0 0 16 16"><path d="M1 2h14M4 5h3v7H4zM9 5h3v4H9z"/></svg></button>
                  <button type="button" class="po-align-btn text-white" title="Center vertically" @click="alignLayer('middle')"><svg viewBox="0 0 16 16"><path d="M1 8h14M4 4h3v8H4zM9 5.5h3v5H9z"/></svg></button>
                  <button type="button" class="po-align-btn text-white" title="Align bottom" @click="alignLayer('bottom')"><svg viewBox="0 0 16 16"><path d="M1 14h14M4 4h3v7H4zM9 7h3v4H9z"/></svg></button>
                </div>
                <small class="po-help text-white">Drag on the canvas to place freely. Arrow keys nudge by 1px, with Shift by 10px.</small>
              </div>
            </div>

            <!-- Nothing selected -->
            <div v-else class="po-sec po-sec--prompt">
              <i class="tim-icons icon-tap-02 text-white"></i>
              <p class="po-help text-white mb-0">Select a layer on the canvas or in the list to edit it. Double click text to edit it in place.</p>
            </div>

            <!-- Overlay settings, always present -->
            <div class="po-sec">
              <div class="po-sec-title text-white"><span>Overlay</span></div>
              <base-input
                id="po-overlay-name"
                label="Name of overlay"
                placeholder="Name"
                :error="errs.overlay_name_error"
                v-model="overlay_name"
                @input="dirty = true; errs.overlay_name_error = ''"
              ></base-input>
              <base-input label="Aspect ratio">
                <el-select v-model="selectedAspectRatio" class="select-primary" placeholder="Aspect ratio" value-key="label" @change="onAspectChange">
                  <el-option v-for="(ar, i) in aspect_ratios" class="select-primary" :label="ar.label" :value="{id: ar.id, label: ar.label}" :key="'AR' + i"></el-option>
                </el-select>
              </base-input>
              <div class="mb-3 po-toggle">
                <el-switch active-color="#e14eca" v-model="crop_to_overlay" @change="dirty = true" />
                <label class="text-white">Crop photo to overlay
                  <el-popover trigger="hover" placement="left" width="240">
                    <div><div class="popover-body" style="word-break: normal; text-align: left">The saved photo is cut to this overlay's shape, keeping the center of the picture. Leave this off for overlays that sit on the full photo, like a logo in a corner.</div></div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
              <base-input label="Description" class="mb-0">
                <el-input v-model="description" :rows="2" type="textarea" placeholder="Shown in the overlay list" @input="dirty = true" />
              </base-input>
            </div>

            <div class="po-sec">
              <div class="po-sec-title text-white"><span>Automation</span></div>
              <div class="mb-2 po-toggle">
                <el-switch active-color="#e14eca" v-model="applied_by_default" @change="dirty = true" />
                <label class="text-white">Apply automatically
                  <el-popover trigger="hover" placement="left" width="240">
                    <div><div class="popover-body" style="word-break: normal; text-align: left">When a photo is taken for one of the selected events, this overlay is added without staff choosing it. Staff can still remove or swap it on the photo.</div></div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
              <template v-if="applied_by_default">
                <base-input label="To photos from">
                  <el-select v-model="selected_game_ids" class="select-primary" placeholder="Select events" multiple @change="dirty = true">
                    <el-option v-for="g in games" class="select-primary" :label="g.name" :value="g.id" :key="'G' + g.id"></el-option>
                  </el-select>
                </base-input>
                <base-input label="When" class="mb-0">
                  <el-select v-model="apply_condition" class="select-primary" placeholder="Select condition" @change="dirty = true">
                    <el-option v-for="c in apply_conditions" class="select-primary" :label="c.label" :value="c.value" :key="c.value"></el-option>
                  </el-select>
                </base-input>
              </template>
            </div>
          </div>
        </aside>
      </div>

      <div class="po-footer">
        <div class="footer-actions">
          <base-button type="secondary" size="sm" :disabled="saving" @click="onCancel">Cancel</base-button>
          <base-button v-if="mode === 0" type="primary" size="sm" :disabled="saving" @click="saveOverlay">Create overlay</base-button>
          <base-button v-else type="primary" size="sm" :disabled="saving" @click="updateOverlay">Save overlay</base-button>
        </div>
      </div>
    </div>

    <base-alert-dialog :show="alertDialog.show"
                       :title="alertDialog.title"
                       :message="alertDialog.message"
                       :variant="alertDialog.variant"
                       :show-cancel="alertDialog.showCancel"
                       :confirm-text="alertDialog.confirmText"
                       :cancel-text="alertDialog.cancelText"
                       :confirm-variant="alertDialog.confirmVariant"
                       @confirm="resolveAlert(true)"
                       @cancel="resolveAlert(false)" />
  </div>
</template>

<script>
import { Popover, Switch } from 'element-ui'
import draggable from 'vuedraggable'
import _ from 'lodash'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION, CDN_URL } from '@/api-config'
import ColorField from '@/components/PaymentPages/ColorField.vue'
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb'
import { designSizeFor, toDesignSpace, aspectMismatch } from '@/util/overlayGeometry'
import { loadFabric, loadFonts, createLayerObject, TEXT_ALIGNS, LAYER_HANDLE_STYLE } from '@/util/overlayRenderer'

// Fonts the picker never offers (icon fonts and two that render badly on canvas).
// Swatches that make sense on a photo; the hex pill takes anything else.
const TEXT_COLOR_PRESETS = ['#ffffff', '#000000', '#e14eca', '#1d8cf8', '#f5d547']
const EXCLUDED_FONTS = ['Libre Baskerville', 'Material Icons', 'Material Icons Outlined', 'Material Icons Sharp', 'Material Icons Round', 'M PLUS Rounded 1c']

function emptyActive() {
  return { id: null, type: '', text: '', font: '', fontSize: 0, color: '', align: 'left', x: 0, y: 0, width: 0, height: 0, file_name: '' }
}

export default {
  name: 'PhotoOverlays',
  components: {
    draggable,
    ColorField,
    BreadcrumbItem,
    Breadcrumb,
    [Switch.name]: Switch,
    [Popover.name]: Popover,
  },
  data() {
    return {
      TEXT_COLOR_PRESETS,
      breadcrumb_name: '',
      mode: 0,
      loading: false,
      saving: false,
      dirty: false,
      fonts: [],
      fontPreviewsLoaded: false,
      games: [],
      // Top layer first. Canvas stacking order is derived from this list.
      layers: [],
      activeLayerId: null,
      active: emptyActive(),
      lockProportions: true,
      sample: { url: '', width: 0, height: 0, name: '' },
      editorTop: 0,
      zoom: 1,
      errs: { overlay_name_error: '' },
      overlay_name: '',
      description: '',
      selectedAspectRatio: { id: 5, label: '16:9' },
      previousAspectRatio: { id: 5, label: '16:9' },
      aspect_ratios: [
        { id: 0, label: '1:1' },
        { id: 1, label: '2:1' },
        { id: 2, label: '3:2' },
        { id: 3, label: '4:3' },
        { id: 4, label: '9:16' },
        { id: 5, label: '16:9' },
        { id: 6, label: '20:9' },
      ],
      applied_by_default: false,
      crop_to_overlay: false,
      selected_game_ids: [],
      apply_condition: 'always',
      apply_conditions: [
        { value: 'escaped', label: 'Event was completed' },
        { value: 'not_escaped', label: 'Event was not completed' },
        { value: 'always', label: 'Always' },
      ],
      dynamic_variables: [
        { id: 0, name: '[Event Name]', description: 'Name of the event associated with the photo.' },
        { id: 1, name: '[Team Name]', description: 'Name of the team if specified. Requires the photo to be tied to a tracker form.' },
        { id: 2, name: '[Company Name]', description: 'The name of your company.' },
        { id: 3, name: '[Location Name]', description: 'The name of the location where the photo was taken.' },
        { id: 4, name: '[Completion Time]', description: 'Recorded time of event completion. Requires the photo to be tied to a tracker form.' },
        { id: 5, name: '[Custom Time]', description: 'Custom time set by the game master denoting event completion. Requires the photo to be tied to a tracker form.' },
        { id: 6, name: '[Time Remaining]', description: 'Remaining time of the total event duration. Requires the photo to be tied to a tracker form.' },
        { id: 7, name: '[Number Of Hints]', description: 'The number of hints provided during the event. Requires the photo to be tied to a tracker form.' },
      ],
      alertDialog: {
        show: false, title: '', message: '', variant: 'warning', showCancel: false,
        confirmText: 'OK', cancelText: 'Cancel', confirmVariant: 'primary',
      },
    }
  },
  computed: {
    editorStyle() {
      if (!this.editorTop) return null
      return { height: 'calc(100vh - ' + (this.editorTop + 24) + 'px)' }
    },
    headerTitle() {
      const name = (this.overlay_name || '').trim()
      if (name) return name
      return this.mode === 0 ? 'New overlay' : (this.breadcrumb_name || 'Edit overlay')
    },
    design() {
      return designSizeFor(this.selectedAspectRatio.label)
    },
    orderedFonts() {
      return _.orderBy(this.fonts.filter(f => !EXCLUDED_FONTS.includes(f.family)), 'family')
    },
    sampleMismatch() {
      if (!this.sample.url || !this.sample.width || !this.sample.height) return false
      return aspectMismatch(this.sample.width / this.sample.height, this.selectedAspectRatio.label)
    },
  },
  created() {
    // Non reactive: Vue must not proxy Fabric internals.
    this.canvas = null
    this.fabric = null
    this.objectsById = new Map()
    this.sampleImage = null
    this.alertResolve = null
    this.guide = null
  },
  methods: {
    // ---------- canvas ----------
    async initCanvas() {
      this.fabric = await loadFabric()
      this.canvas = new this.fabric.Canvas(this.$refs.canvas, {
        preserveObjectStacking: true,
        selection: false,
      })
      this.canvas.on('selection:created', e => this.onSelection(e.selected && e.selected[0]))
      this.canvas.on('selection:updated', e => this.onSelection(e.selected && e.selected[0]))
      this.canvas.on('selection:cleared', () => this.onSelection(null))
      this.canvas.on('object:modified', e => this.onObjectModified(e.target))
      this.canvas.on('text:changed', e => this.onTextChanged(e.target))
      this.canvas.on('object:moving', () => this.updateAnchorGuide())
      this.canvas.on('object:scaling', () => this.updateAnchorGuide())
      this.createAnchorGuide()
      this.fitCanvas()
    },
    fitCanvas() {
      if (!this.canvas || !this.$refs.stage) return
      const design = this.design
      const available = Math.max(this.$refs.stage.clientWidth - 48, 120)
      const maxHeight = Math.max(200, this.$refs.stage.clientHeight - 48)
      const zoom = Math.min(available / design.width, maxHeight / design.height)
      this.applyZoom(zoom)
    },
    // Size the editor shell to the viewport so Cancel and Save stay on screen
    // without page scroll. Measured from the shell's own top edge so the
    // breadcrumb and navbar heights are never hard coded. Mobile stacks the
    // panels and scrolls the page instead.
    measureEditor() {
      const el = this.$refs.editor
      if (!el) return
      if (window.innerWidth < 992) { this.editorTop = 0; return }
      const rect = el.getBoundingClientRect()
      this.editorTop = Math.round(rect.top + window.scrollY)
    },
    applyZoom(zoom) {
      const design = this.design
      this.zoom = zoom
      this.canvas.setDimensions({ width: Math.round(design.width * zoom), height: Math.round(design.height * zoom) })
      this.canvas.setZoom(zoom)
      this.canvas.requestRenderAll()
    },
    zoomBy(factor) {
      if (!this.canvas) return
      this.applyZoom(Math.min(4, Math.max(0.05, this.zoom * factor)))
    },
    clearSelection() {
      if (!this.canvas) return
      this.canvas.discardActiveObject()
      this.canvas.requestRenderAll()
      this.onSelection(null)
    },
    insertVariable(variable) {
      const obj = this.activeObject()
      if (!obj || obj.layerType !== 'text' || !variable) return
      const current = obj.text || ''
      const next = current.trim() === '' ? variable : current + ' ' + variable
      obj.set('text', next)
      this.active.text = next
      const record = this.layers.find(l => l.id === obj.layerId)
      if (record) record.text = next
      this.commit(obj)
    },
    newLayerId() {
      return 'L' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
    },
    registerObject(obj, record) {
      obj.layerId = record.id
      obj.set(LAYER_HANDLE_STYLE)
      this.objectsById.set(record.id, obj)
    },
    addObjectToCanvas(obj, record, { select = true } = {}) {
      this.registerObject(obj, record)
      this.canvas.add(obj)
      if (this.guide) this.canvas.bringObjectToFront(this.guide)
      this.layers.unshift(record)
      if (select) {
        this.canvas.setActiveObject(obj)
      }
      this.canvas.requestRenderAll()
    },
    objectFor(layerId) {
      return this.objectsById.get(layerId) || null
    },
    layerLabel(layer) {
      if (layer.type === 'text') return (layer.text || '').trim() || 'Text'
      return layer.file_name || 'Image'
    },
    // ---------- selection and sync ----------
    onSelection(obj) {
      if (!obj || !obj.layerId) {
        this.activeLayerId = null
        this.active = emptyActive()
        this.updateAnchorGuide()
        return
      }
      this.activeLayerId = obj.layerId
      this.syncActiveFromObject(obj)
      this.updateAnchorGuide()
    },
    syncActiveFromObject(obj) {
      const record = this.layers.find(l => l.id === obj.layerId) || {}
      this.active = {
        id: obj.layerId,
        type: obj.layerType,
        text: obj.layerType === 'text' ? obj.text : '',
        font: obj.layerType === 'text' ? obj.fontFamily : '',
        fontSize: obj.layerType === 'text' ? Math.round(obj.fontSize) : 0,
        color: obj.layerType === 'text' ? obj.fill : '',
        align: obj.layerType === 'text' ? (obj.originX || 'left') : 'left',
        x: Math.round(obj.left),
        y: Math.round(obj.top),
        width: Math.round(obj.getScaledWidth()),
        height: Math.round(obj.getScaledHeight()),
        file_name: record.file_name || '',
      }
      if (obj.layerType === 'image') {
        this.lockProportions = obj.lockUniScaling !== false
      }
    },
    onObjectModified(obj) {
      if (!obj) return
      if (obj.layerType === 'text' && (obj.scaleX !== 1 || obj.scaleY !== 1)) {
        obj.set({ fontSize: Math.round(obj.fontSize * obj.scaleY), scaleX: 1, scaleY: 1 })
        obj.setCoords()
        this.canvas.requestRenderAll()
      }
      this.dirty = true
      if (obj.layerId === this.activeLayerId) this.syncActiveFromObject(obj)
      this.updateAnchorGuide()
    },
    onTextChanged(obj) {
      if (!obj) return
      const record = this.layers.find(l => l.id === obj.layerId)
      if (record) record.text = obj.text
      if (obj.layerId === this.activeLayerId) this.active.text = obj.text
      this.dirty = true
      this.updateAnchorGuide()
    },
    selectLayer(layerId) {
      const obj = this.objectFor(layerId)
      if (!obj) return
      this.canvas.setActiveObject(obj)
      this.canvas.requestRenderAll()
    },
    // ---------- layer list ----------
    addTextLayer() {
      const record = { id: this.newLayerId(), type: 'text', text: 'New text', file_name: '', url: '' }
      createLayerObject(this.fabric, {
        type: 'text', content: record.text, x: 100, y: 100, text_align: 'left',
        fontSize: Math.round(this.design.width / 20), font: 'Poppins', color: '#000000',
      }).then(obj => {
        this.addObjectToCanvas(obj, record)
        this.dirty = true
      })
    },
    removeLayer(layerId) {
      const obj = this.objectFor(layerId)
      if (obj) {
        this.canvas.remove(obj)
        this.objectsById.delete(layerId)
      }
      this.layers = this.layers.filter(l => l.id !== layerId)
      if (this.activeLayerId === layerId) {
        this.canvas.discardActiveObject()
        this.onSelection(null)
      }
      this.canvas.requestRenderAll()
      this.dirty = true
    },
    onLayerReorder() {
      // layers is top first; canvas index 0 is the bottom.
      const count = this.layers.length
      this.layers.forEach((record, listIndex) => {
        const obj = this.objectFor(record.id)
        if (obj) this.canvas.moveObjectTo(obj, count - 1 - listIndex)
      })
      if (this.guide) this.canvas.bringObjectToFront(this.guide)
      this.canvas.requestRenderAll()
      this.dirty = true
    },
    // ---------- properties panel ----------
    activeObject() {
      return this.active.id ? this.objectFor(this.active.id) : null
    },
    commit(obj) {
      obj.setCoords()
      this.updateAnchorGuide()
      this.canvas.requestRenderAll()
      this.dirty = true
      this.syncActiveFromObject(obj)
    },
    setText(value) {
      const obj = this.activeObject()
      if (!obj || obj.layerType !== 'text') return
      obj.set('text', value)
      const record = this.layers.find(l => l.id === obj.layerId)
      if (record) record.text = value
      obj.setCoords()
      this.canvas.requestRenderAll()
      this.dirty = true
    },
    // Each option shows its own typeface. The list is about a hundred families,
    // so when it first opens we load one stylesheet per batch of families
    // subset to the letters of their names (a few hundred bytes each) instead
    // of the full fonts. The chosen font is still loaded in full by onFontChange.
    onFontListToggle(open) {
      if (!open || this.fontPreviewsLoaded || this.orderedFonts.length === 0) return
      this.fontPreviewsLoaded = true
      const families = this.orderedFonts.map(f => f.family)
      const BATCH = 25
      for (let i = 0; i < families.length; i += BATCH) {
        const batch = families.slice(i, i + BATCH)
        const chars = [...new Set(batch.join(''))].join('')
        const query = batch.map(f => 'family=' + encodeURIComponent(f).replace(/%20/g, '+')).join('&')
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'https://fonts.googleapis.com/css2?' + query + '&text=' + encodeURIComponent(chars) + '&display=swap'
        document.head.appendChild(link)
      }
    },
    async onFontChange(family) {
      const obj = this.activeObject()
      if (!obj || obj.layerType !== 'text') return
      await loadFonts([family])
      obj.set('fontFamily', family)
      obj.initDimensions()
      this.commit(obj)
    },
    setFontSize(value) {
      const obj = this.activeObject()
      if (!obj || obj.layerType !== 'text' || !value) return
      obj.set('fontSize', Number(value))
      this.commit(obj)
    },
    setColor(value) {
      const obj = this.activeObject()
      if (!obj || obj.layerType !== 'text') return
      obj.set('fill', value || '#000000')
      this.commit(obj)
    },
    // Align the selected layer to the artboard edges or center.
    // The anchor guide is a real canvas object (dashed line) so Fabric redraws
    // it with everything else. It is never selectable, never a layer and never
    // saved: every enumeration of canvas objects filters on layerId.
    createAnchorGuide() {
      this.guide = new this.fabric.Line([0, 0, 0, 0], {
        stroke: '#e14eca',
        strokeWidth: 1.5,
        strokeDashArray: [4, 3],
        strokeUniform: true,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        objectCaching: false,
        visible: false,
        originX: 'center',
        originY: 'top',
      })
      this.canvas.add(this.guide)
    },
    updateAnchorGuide() {
      if (!this.guide || !this.canvas) return
      const obj = this.canvas.getActiveObject()
      if (!obj || obj.layerType !== 'text' || obj.isEditing) {
        if (this.guide.visible) { this.guide.set('visible', false); this.canvas.requestRenderAll() }
        return
      }
      const pad = 14
      const h = obj.getScaledHeight()
      this.guide.set({ x1: obj.left, x2: obj.left, y1: obj.top - pad, y2: obj.top + h + pad, visible: true })
      this.guide.setCoords()
      this.canvas.bringObjectToFront(this.guide)
      this.canvas.requestRenderAll()
    },
    // Distance from a text object's left edge to its anchor (originX).
    anchorOffsetX(obj) {
      const w = obj.getScaledWidth()
      if (obj.originX === 'center') return w / 2
      if (obj.originX === 'right') return w
      return 0
    },
    // Change a text layer's anchor without moving it on screen: keep the left
    // edge where it is and move `left` (the anchor) to the new origin.
    setTextAlign(align) {
      const obj = this.activeObject()
      if (!obj || obj.layerType !== 'text' || !TEXT_ALIGNS.includes(align)) return
      const leftEdge = obj.left - this.anchorOffsetX(obj)
      obj.set({ originX: align, textAlign: align })
      obj.set('left', Math.round(leftEdge + this.anchorOffsetX(obj)))
      this.commit(obj)
    },
    // Horizontal alignment also anchors text to the same side, so a dynamic
    // variable that resolves shorter or longer stays aligned on the photo.
    alignLayer(where) {
      const obj = this.activeObject()
      if (!obj) return
      if (obj.layerType === 'text' && TEXT_ALIGNS.includes(where)) {
        obj.set({ originX: where, textAlign: where })
      }
      const w = obj.getScaledWidth()
      const h = obj.getScaledHeight()
      const d = this.design
      const ox = this.anchorOffsetX(obj)
      if (where === 'left') obj.set('left', Math.round(ox))
      if (where === 'center') obj.set('left', Math.round((d.width - w) / 2 + ox))
      if (where === 'right') obj.set('left', Math.round(d.width - w + ox))
      if (where === 'top') obj.set('top', 0)
      if (where === 'middle') obj.set('top', Math.round((d.height - h) / 2))
      if (where === 'bottom') obj.set('top', Math.round(d.height - h))
      this.commit(obj)
    },
    nudgeLayer(dx, dy) {
      const obj = this.activeObject()
      if (!obj) return
      obj.set({ left: Math.round(obj.left + dx), top: Math.round(obj.top + dy) })
      this.commit(obj)
    },
    setImageWidth(value) {
      const obj = this.activeObject()
      if (!obj || obj.layerType !== 'image' || !value) return
      const scaleX = Number(value) / obj.width
      obj.set('scaleX', scaleX)
      if (this.lockProportions) obj.set('scaleY', scaleX)
      this.commit(obj)
    },
    setImageHeight(value) {
      const obj = this.activeObject()
      if (!obj || obj.layerType !== 'image' || !value) return
      const scaleY = Number(value) / obj.height
      obj.set('scaleY', scaleY)
      if (this.lockProportions) obj.set('scaleX', scaleY)
      this.commit(obj)
    },
    setLockProportions(value) {
      const obj = this.activeObject()
      if (!obj || obj.layerType !== 'image') return
      obj.set('lockUniScaling', value === true)
      this.canvas.requestRenderAll()
    },
    // ---------- aspect ratio ----------
    async onAspectChange(next) {
      const previous = this.previousAspectRatio
      if (!previous || previous.label === next.label) {
        this.previousAspectRatio = next
        this.$nextTick(() => this.fitCanvas())
        return
      }
      if (this.layers.length === 0) {
        this.previousAspectRatio = next
        this.dirty = true
        this.$nextTick(() => this.fitCanvas())
        return
      }
      const ok = await this.showAlert({
        title: 'Change aspect ratio?',
        message: 'Layers are repositioned proportionally. Check the preview before saving.',
        variant: 'warning',
        showCancel: true,
        confirmText: 'Change',
        confirmVariant: 'primary',
      })
      if (!ok) {
        this.selectedAspectRatio = previous
        return
      }
      const from = designSizeFor(previous.label)
      const to = designSizeFor(next.label)
      const sx = to.width / from.width
      const sy = to.height / from.height
      for (const obj of this.canvas.getObjects().filter(o => o.layerId)) {
        obj.set({ left: obj.left * sx, top: obj.top * sy })
        if (obj.layerType === 'image') {
          obj.set({ scaleX: obj.scaleX * sx, scaleY: obj.scaleY * sy })
        } else {
          obj.set({ fontSize: obj.fontSize * sx })
        }
        obj.setCoords()
      }
      this.previousAspectRatio = next
      this.dirty = true
      this.$nextTick(() => {
        this.fitCanvas()
        this.positionSample()
        const activeObj = this.activeObject()
        if (activeObj) this.syncActiveFromObject(activeObj)
      })
    },
    // ---------- sample backdrop ----------
    async setSample(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      this.clearSample()
      const url = URL.createObjectURL(file)
      try {
        const img = await this.fabric.FabricImage.fromURL(url)
        this.sample = { url, width: img.width, height: img.height, name: file.name }
        this.sampleImage = img
        this.positionSample()
      } catch (e) {
        console.error('Sample photo failed to load', e)
        URL.revokeObjectURL(url)
        this.showAlert({ title: 'Error', message: 'That file could not be opened as an image.', variant: 'danger' })
      }
    },
    positionSample() {
      if (!this.canvas) return
      const img = this.sampleImage
      if (!img) {
        this.canvas.backgroundImage = null
        this.canvas.requestRenderAll()
        return
      }
      const design = this.design
      const s = Math.min(img.width / design.width, img.height / design.height)
      img.set({
        originX: 'left',
        originY: 'top',
        scaleX: 1 / s,
        scaleY: 1 / s,
        left: (design.width - img.width / s) / 2,
        top: (design.height - img.height / s) / 2,
      })
      this.canvas.backgroundImage = img
      this.canvas.requestRenderAll()
    },
    clearSample() {
      if (this.sample.url) URL.revokeObjectURL(this.sample.url)
      this.sample = { url: '', width: 0, height: 0, name: '' }
      this.sampleImage = null
      this.positionSample()
    },
    // ---------- image upload ----------
    getAuthToken() {
      return new Promise(resolve => {
        Auth.currentSession()
          .then(data => resolve(data.idToken.jwtToken))
          .catch(err => console.log(err))
      })
    },
    getUrlKey(url) {
      const urlObject = new URL(url)
      return urlObject.pathname.split('/')[1]
    },
    async getSignedURL(file) {
      const token = await this.getAuthToken()
      const config = { headers: { Authorization: token } }
      const url = API_LOCATION + 'upload/photos?bucket=1'
      const payload = { filePath: file.name, contentType: file.type }
      const res = await axios.post(url, payload, config)
      return res.data
    },
    async uploadFile(signedUrl, file) {
      const config = { headers: { 'Content-Type': file.type } }
      return axios.put(signedUrl, file, config)
    },
    async uploadImage(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      this.loading = true
      try {
        const signedUrl = await this.getSignedURL(file)
        await this.uploadFile(signedUrl, file)
        const imgUrl = CDN_URL + this.getUrlKey(signedUrl)
        const obj = await createLayerObject(this.fabric, { type: 'image', content: imgUrl, x: 0, y: 0 })
        const design = this.design
        const s = Math.min(design.width / obj.width, design.height / obj.height, 1)
        obj.set({ scaleX: s, scaleY: s, lockUniScaling: true })
        const record = { id: this.newLayerId(), type: 'image', text: '', file_name: file.name, url: imgUrl }
        this.addObjectToCanvas(obj, record)
        this.dirty = true
      } catch (err) {
        console.error(err)
        this.handleApiError(err)
      } finally {
        this.loading = false
      }
    },
    // ---------- payload ----------
    serializeLayers() {
      const objects = this.canvas.getObjects().filter(o => o.layerId)
      return objects.map((obj, index) => {
        const record = this.layers.find(l => l.id === obj.layerId) || {}
        const isText = obj.layerType === 'text'
        return {
          type: isText ? 'text' : 'image',
          position: index,
          content: isText ? obj.text : record.url,
          x: Math.round(obj.left),
          y: Math.round(obj.top),
          width: Math.round(obj.getScaledWidth()),
          height: Math.round(obj.getScaledHeight()),
          fontSize: isText ? Math.round(obj.fontSize) : 20,
          font: isText ? obj.fontFamily : 'Poppins',
          color: isText ? obj.fill : '#000000',
          text_align: isText ? (obj.originX || 'left') : 'left',
          file_name: record.file_name || '',
          object_fit: isText ? 'cover' : 'fill',
        }
      })
    },
    buildPayload() {
      let gameDefaults = []
      if (this.applied_by_default && this.selected_game_ids.length > 0) {
        gameDefaults = this.selected_game_ids.map(gameId => ({ game_id: gameId, apply_condition: this.apply_condition }))
      }
      return {
        name: this.overlay_name,
        layers: this.serializeLayers(),
        aspect_ratio: this.selectedAspectRatio.label,
        description: this.description,
        reference_height: this.design.height,
        reference_width: this.design.width,
        applied_by_default: this.applied_by_default,
        crop_to_overlay: this.crop_to_overlay,
        game_defaults: gameDefaults,
      }
    },
    validate() {
      if (this.overlay_name.trim() === '') {
        this.errs.overlay_name_error = 'Overlay name cannot be empty'
        this.$notify({ type: 'danger', message: 'Give the overlay a name before saving', icon: 'tim-icons icon-alert-circle-exc' })
        this.$nextTick(() => {
          const wrap = document.getElementById('po-overlay-name')
          const el = wrap && (wrap.tagName === 'INPUT' ? wrap : wrap.querySelector('input'))
          if (el) el.focus()
        })
        return false
      }
      this.errs.overlay_name_error = ''
      return true
    },
    async saveOverlay() {
      if (!this.validate()) return
      this.saving = true
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const url = API_LOCATION + 'companies/' + companyId + '/photo-settings/' + this.$route.query.id + '/overlays'
        await axios.post(url, this.buildPayload(), config)
        this.dirty = false
        this.$notify({ type: 'success', message: 'Overlay created', icon: 'tim-icons icon-check-2' })
        this.$router.push({ path: '/photos/settings', query: { id: this.$route.query.id } })
      } catch (err) {
        this.handleApiError(err)
      } finally {
        this.saving = false
        this.loading = false
      }
    },
    async updateOverlay() {
      if (!this.validate()) return
      this.saving = true
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const url = API_LOCATION + 'companies/' + companyId + '/photo-settings/' + this.$route.query.id + '/overlays/' + this.$route.query.oid
        await axios.put(url, this.buildPayload(), config)
        this.dirty = false
        this.breadcrumb_name = this.overlay_name
        this.$notify({ type: 'success', message: 'Overlay updated', icon: 'tim-icons icon-check-2' })
      } catch (err) {
        this.handleApiError(err)
      } finally {
        this.saving = false
        this.loading = false
      }
    },
    // ---------- load ----------
    async getOverlay() {
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const url = API_LOCATION + 'companies/' + companyId + '/photo-settings/' + this.$route.query.id + '/overlays/' + this.$route.query.oid
        const response = await axios.get(url, config)
        const overlay = response.data.overlay
        this.overlay_name = overlay.name
        this.breadcrumb_name = overlay.name
        this.description = overlay.description || ''
        this.selectedAspectRatio = _.find(this.aspect_ratios, { label: overlay.aspect_ratio }) || { id: 5, label: '16:9' }
        this.previousAspectRatio = this.selectedAspectRatio
        this.applied_by_default = Boolean(overlay.applied_by_default)
        this.crop_to_overlay = Number(overlay.crop_to_overlay) === 1
        if (response.data.game_defaults && response.data.game_defaults.length > 0) {
          this.selected_game_ids = response.data.game_defaults.map(gd => gd.game_id)
          this.apply_condition = response.data.game_defaults[0].apply_condition || 'always'
        }

        const design = designSizeFor(overlay.aspect_ratio)
        const elements = _.orderBy(response.data.elements || [], e => Number(e.position))
        const layers = toDesignSpace(elements, overlay.reference_width, overlay.reference_height, design)
        await loadFonts(layers.filter(l => l.type === 'text').map(l => l.font))

        this.canvas.clear()
        this.createAnchorGuide()
        this.objectsById = new Map()
        this.layers = []
        for (const layer of layers) {
          try {
            const obj = await createLayerObject(this.fabric, layer)
            const record = {
              id: this.newLayerId(),
              type: layer.type,
              text: layer.type === 'text' ? layer.content : '',
              file_name: layer.file_name || '',
              url: layer.type === 'image' ? layer.content : '',
            }
            this.addObjectToCanvas(obj, record, { select: false })
          } catch (e) {
            console.error('Overlay layer failed to load', e)
          }
        }
        this.positionSample()
        this.$nextTick(() => this.fitCanvas())
        this.dirty = false
      } catch (err) {
        this.handleApiError(err)
      } finally {
        this.loading = false
      }
    },
    getFonts() {
      axios.get(API_LOCATION + 'fonts')
        .then(response => { this.fonts = response.data })
        .catch(err => {
          console.log(err)
          this.handleApiError(err)
        })
    },
    async getGames() {
      const token = await this.getAuthToken()
      const config = { headers: { Authorization: token } }
      const companyId = localStorage.getItem('companyId')
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id
      const url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/games'
      axios.get(url, config)
        .then(response => { this.games = response.data })
        .catch(err => console.log(err))
    },
    // ---------- navigation, dialogs, errors ----------
    async onCancel() {
      if (this.dirty) {
        const ok = await this.showAlert({
          title: 'Discard changes?',
          message: 'Your unsaved changes to this overlay will be lost.',
          variant: 'warning',
          showCancel: true,
          confirmText: 'Discard',
          confirmVariant: 'danger',
        })
        if (!ok) return
      }
      this.dirty = false
      this.$router.push({ path: '/photos/settings', query: { id: this.$route.query.id } })
    },
    copy(text) {
      navigator.clipboard.writeText(text)
      this.$notify({ type: 'success', message: 'Copied', icon: 'tim-icons icon-check-2' })
    },
    onKeydown(e) {
      if (this.alertDialog.show) return
      const tag = (e.target && e.target.tagName) || ''
      const inField = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable)
      if (e.key === 'Escape') {
        if (!this.canvas) return
        const obj = this.canvas.getActiveObject()
        if (obj && obj.isEditing) return
        this.canvas.discardActiveObject()
        this.canvas.requestRenderAll()
        return
      }
      if (!inField && this.canvas && e.key.startsWith('Arrow')) {
        const obj = this.canvas.getActiveObject()
        if (!obj || obj.isEditing || !obj.layerId) return
        e.preventDefault()
        const step = e.shiftKey ? 10 : 1
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0
        this.nudgeLayer(dx, dy)
        return
      }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      if (inField || !this.canvas) return
      const obj = this.canvas.getActiveObject()
      if (!obj || obj.isEditing || !obj.layerId) return
      e.preventDefault()
      this.removeLayer(obj.layerId)
    },
    // Promise-based wrapper around the shared base-alert-dialog. Resolves true
    // on confirm, false on cancel or dismiss.
    showAlert(opts) {
      if (this.alertResolve) this.alertResolve(false)
      this.alertDialog = {
        show: true,
        title: opts.title || '',
        message: opts.message || '',
        variant: opts.variant || 'warning',
        showCancel: opts.showCancel === true,
        confirmText: opts.confirmText || 'OK',
        cancelText: opts.cancelText || 'Cancel',
        confirmVariant: opts.confirmVariant || 'primary',
      }
      return new Promise(resolve => { this.alertResolve = resolve })
    },
    resolveAlert(ok) {
      this.alertDialog.show = false
      const resolve = this.alertResolve
      this.alertResolve = null
      if (resolve) resolve(ok)
    },
    async handleApiError(err) {
      console.error(err)
      const status = err && err.response && err.response.status
      if (status === 401) {
        await this.showAlert({
          title: 'Error',
          message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
          variant: 'danger',
        })
        this.$router.push('/')
        return
      }
      this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
    },
  },
  async mounted() {
    this.getFonts()
    this.getGames()
    this.measureEditor()
    this.onResize = _.debounce(() => { this.measureEditor(); this.$nextTick(() => this.fitCanvas()) }, 150)
    window.addEventListener('resize', this.onResize)
    window.addEventListener('keydown', this.onKeydown)
    await this.initCanvas()
    if (Object.prototype.hasOwnProperty.call(this.$route.query, 'oid')) {
      this.mode = 1
      await this.getOverlay()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('keydown', this.onKeydown)
    if (this.onResize && this.onResize.cancel) this.onResize.cancel()
    this.clearSample()
    if (this.canvas) {
      this.canvas.dispose()
      this.canvas = null
    }
  },
}
</script>

<style scoped>
/* Studio layout for a full-page canvas editor: header, tool rail, artboard,
   contextual inspector with the layer list docked below. No house primitive
   covers an editor shell, so this file owns it. Inputs, buttons, the drag
   handle, the remove X and the badge come from the global primitives. */
.po-editor {
  display: flex;
  flex-direction: column;
  background: var(--po-panel, #22233a);
  border-radius: 0.375rem;
  border: 1px solid rgba(125, 125, 125, 0.28);
  overflow: hidden;
  min-height: 520px;
}
.po-breadcrumb { padding-top: 0; padding-bottom: 0.25rem; margin-bottom: 0.5rem; }
.po-topbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(125, 125, 125, 0.28);
  flex-wrap: wrap;
}
.po-topbar-right { margin-left: auto; display: flex; align-items: center; gap: 10px; }
.po-title { font-size: 1.1rem; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
.po-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid rgba(125, 125, 125, 0.28);
  flex-wrap: wrap;
}

.po-body { display: flex; flex: 1; min-height: 0; }

.po-left {
  width: 240px;
  flex-shrink: 0;
  border-right: 1px solid rgba(125, 125, 125, 0.28);
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.po-file { display: none; }
.po-tools {
  display: flex;
  gap: 4px;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(125, 125, 125, 0.28);
}
.po-tool {
  flex: 1;
  height: 52px;
  border-radius: 0.375rem;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font: inherit;
  font-size: 10px;
  margin: 0;
  padding: 0;
}
.po-tool i { font-size: 18px; }
.po-tool span { font-weight: 500; }
.po-tool:hover { background: rgba(225, 78, 202, 0.16); }
.po-tool--on { background: rgba(225, 78, 202, 0.16); color: #e14eca !important; }
.po-tool:focus-visible { outline: 2px solid #e14eca; }

.po-stage { flex: 1; min-width: 0; display: flex; flex-direction: column; background: rgba(0, 0, 0, 0.18); }
.po-stage-inner {
  flex: 1;
  display: flex;
  padding: 24px;
  min-height: 0;
  overflow: auto;
}
.po-artboard {
  /* margin auto centers inside the scroll box without clipping the top-left
     edge when the artboard is zoomed larger than the stage */
  margin: auto;
  position: relative;
  display: inline-block;
  line-height: 0;
  border: 1px solid rgba(125, 125, 125, 0.4);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
  background-color: rgba(125, 125, 125, 0.12);
  background-image:
    linear-gradient(45deg, rgba(125, 125, 125, 0.18) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(125, 125, 125, 0.18) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(125, 125, 125, 0.18) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(125, 125, 125, 0.18) 75%);
  background-size: 18px 18px;
  background-position: 0 0, 0 9px, 9px -9px, -9px 0;
}
.po-artboard--photo { background-image: none; }
.po-stagebar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-top: 1px solid rgba(125, 125, 125, 0.28);
  font-size: 12px;
  flex-wrap: wrap;
}
.po-stagebar-size { font-variant-numeric: tabular-nums; }
.po-stagebar-muted { opacity: 0.75; }
.po-stagebar .btn { margin: 0; }
.po-pill { font-size: 10px; padding: 2px 8px; border-radius: 999px; background: rgba(225, 78, 202, 0.16); color: #e14eca; font-weight: 600; }
.po-pill--warn { background: rgba(255, 141, 114, 0.18); color: #ff8d72; }
.po-zoom { margin-left: auto; display: flex; gap: 6px; align-items: center; }
.po-zoom-label { font-size: 11px; opacity: 0.75; margin-right: 2px; }
.po-zoom-value { min-width: 38px; text-align: center; font-variant-numeric: tabular-nums; }
.po-zoom-btn {
  font: inherit;
  font-size: 12px;
  line-height: 1;
  width: 24px;
  height: 24px;
  border-radius: 0.375rem;
  border: 1px solid rgba(125, 125, 125, 0.5);
  background: rgba(125, 125, 125, 0.12);
  cursor: pointer;
}
.po-zoom-btn--wide { width: auto; padding: 0 8px; font-size: 11px; }
.po-zoom-btn:hover { background: rgba(225, 78, 202, 0.2); }

.po-inspector {
  width: 320px;
  flex-shrink: 0;
  border-left: 1px solid rgba(125, 125, 125, 0.28);
  display: flex;
  flex-direction: column;
  min-height: 0;
}
/* overflow-x hidden: bootstrap .row gutters are wider than the section
   padding, which otherwise leaves a 1px horizontal scrollbar. */
.po-ins-scroll { flex: 1; min-height: 0; overflow-x: hidden; overflow-y: auto; }
.po-sec .row { margin-left: -6px; margin-right: -6px; }
.po-sec .row > [class*="col-"] { padding-left: 6px; padding-right: 6px; }
.po-toggle { display: flex; align-items: center; gap: 8px; }
.po-toggle label { margin-bottom: 0; font-size: 0.75rem; }
.po-align { margin-bottom: 4px; }
.po-align label { margin-bottom: 4px; }
.po-align-row { display: flex; align-items: center; gap: 4px; margin-bottom: 6px; }
.po-align-gap { width: 8px; }
.po-align-btn {
  width: 34px;
  height: 30px;
  border-radius: 0.375rem;
  border: 1px solid rgba(125, 125, 125, 0.35);
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.po-align-btn svg { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 1.4; stroke-linecap: round; stroke-linejoin: round; }
.po-align-btn:hover,
.po-align-btn--on { background: rgba(225, 78, 202, 0.16); border-color: rgba(225, 78, 202, 0.6); color: #e14eca !important; }
.po-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }
.po-chip {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(125, 125, 125, 0.35);
  background: transparent;
  cursor: pointer;
  line-height: 1.3;
}
.po-chip:hover { border-color: rgba(225, 78, 202, 0.6); background: rgba(225, 78, 202, 0.12); color: #e14eca; }
.po-colorfield { padding: 0; margin-bottom: 12px; }
.po-colorfield ::v-deep .cf-color__label { font-size: 0.75rem; font-weight: 400; }
.po-sec--prompt { padding: 28px 18px; text-align: center; }
.po-sec--prompt i { display: block; font-size: 22px; opacity: 0.6; margin-bottom: 8px; }
.po-sec--prompt .po-help { opacity: 0.75; max-width: 240px; margin: 0 auto; }
.po-sec { padding: 12px 14px; border-bottom: 1px solid rgba(125, 125, 125, 0.28); }
.po-sec-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}
.po-sec-title small { font-weight: 400; text-transform: none; letter-spacing: 0; font-size: 11px; opacity: 0.75; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.po-filename { display: flex; align-items: center; gap: 8px; font-size: 13px; margin: 2px 0 16px; }
.po-filename i { font-size: 14px; flex: none; opacity: 0.75; }
.po-sec-action {
  width: 26px;
  height: 26px;
  margin: -6px 0;
  padding: 0;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.6;
}
.po-sec-action i { font-size: 14px; }
.po-sec-action:hover { opacity: 1; background: rgba(125, 125, 125, 0.18); }
.po-filename span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.po-hint { margin-top: 6px; }
.po-help { display: block; font-size: 11.5px; opacity: 0.75; }

.po-layers { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.po-layers-title { padding: 12px 14px 6px; margin: 0; align-items: center; }
/* Same number chip as TabRail's tab count: filled neutral grey, right after the label. */
.po-count {
  display: inline-block;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  margin-left: auto;
  border-radius: 8px;
  background: rgba(125, 125, 125, 0.25);
  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
  text-align: center;
  vertical-align: 1px;
  letter-spacing: 0;
  opacity: 0.85;
}
.po-layers-empty { padding: 0 14px 12px; margin: 0; }
.po-layers-list { overflow: auto; flex: 1; min-height: 0; }
.po-layer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  margin: 0 8px 4px;
  border-radius: 0.375rem;
  cursor: pointer;
}
.po-layer:hover { background: rgba(125, 125, 125, 0.12); }
.po-layer--active { background: rgba(225, 78, 202, 0.15); }
.po-layer-icon { opacity: 0.7; font-size: 12px; }
.po-layer__label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; }

@media (max-width: 991px) {
  .po-editor { min-height: 0; height: auto !important; }
  .po-body { flex-direction: column; }
  .po-left { width: 100%; border-right: none; border-bottom: 1px solid rgba(125, 125, 125, 0.28); }
  .po-layers-list { max-height: 220px; }
  .po-inspector { width: 100%; border-left: none; border-top: 1px solid rgba(125, 125, 125, 0.28); }
  .po-ins-scroll, .po-layers { max-height: none; overflow: visible; }
  .po-stage-inner { padding: 16px 12px; min-height: 260px; }
}
</style>

<style>
.white-content .po-editor { background: #ffffff; }
.white-content .po-stage { background: rgba(0, 0, 0, 0.04); }
</style>
